'use client'

import { useAtom } from 'jotai'
import { useEffect, useRef } from 'react'

import {
  AUTO_HIDE_DAYS_OPTIONS,
  DEFAULT_AUTO_HIDE_DAYS,
} from '@/features/dashboard/lib/page-visibility'
import { getComponentPreference, saveComponentPreference } from '@/service/ui-state.api'
import {
  hiddenPageIdsAtom,
  pageAutoHideDaysAtom,
  pageLastUsedAtAtom,
  pageTabsOrderAtom,
  pinnedPageIdsAtom,
} from '@/state/storage'

const PAGE_TABS_COMPONENT_ID = 'page-tabs'
const SAVE_DEBOUNCE_MS = 800

interface SyncedPageTabsState {
  order: string[]
  hidden: string[]
  pinned: string[]
  lastUsedAt: Record<string, number>
  autoHideDays: number
}

// The fields a user edits directly. Kept separate from lastUsedAt, which drifts
// on its own via ordinary navigation — folding it into the hydration race-guard
// below would make that guard fail on virtually every load for a returning user.
type SyncedPrefs = Omit<SyncedPageTabsState, 'lastUsedAt'>

const EMPTY_PREFS: SyncedPrefs = {
  order: [],
  hidden: [],
  pinned: [],
  autoHideDays: DEFAULT_AUTO_HIDE_DAYS,
}

// Module scope, not refs: PageTabs remounts on the mobile/desktop breakpoint switch,
// and hydration must run once per user session, not once per mount.
let hydratedUserKey: string | null = null
// Saves are armed only once the hydration fetch has SETTLED for this user. Arming any
// earlier would let the debounce timer upload the pre-hydration localStorage state over
// a fresher server copy whenever the fetch takes longer than the debounce.
let saveArmedUserKey: string | null = null
let lastSyncedSnapshot: string | null = null

/**
 * Called from the logout reset path (use-reset-client-storage): the atoms are wiped
 * there, so the module flags must drop too or a same-user re-login in the same JS
 * session would skip hydration and never restore the server-saved state.
 */
export function resetPageTabsSyncState() {
  hydratedUserKey = null
  saveArmedUserKey = null
  lastSyncedSnapshot = null
}

export function parseConfigField<T>(
  config: Record<string, string> | undefined,
  key: string,
  fallback: T
): T {
  const raw = config?.[key]
  if (!raw) return fallback
  try {
    return JSON.parse(raw) as T
  } catch {
    return fallback
  }
}

export function parseAutoHideDays(config: Record<string, string> | undefined): number {
  const parsed = parseConfigField(config, 'autoHideDays', DEFAULT_AUTO_HIDE_DAYS)
  return (AUTO_HIDE_DAYS_OPTIONS as readonly number[]).includes(parsed)
    ? parsed
    : DEFAULT_AUTO_HIDE_DAYS
}

function isEmptyPrefs(prefs: SyncedPrefs): boolean {
  return (
    !prefs.order.length &&
    !prefs.hidden.length &&
    !prefs.pinned.length &&
    prefs.autoHideDays === DEFAULT_AUTO_HIDE_DAYS
  )
}

// Only gates the very first save of a session, for brand-new users with nothing
// to upload yet. Unlike isEmptyPrefs, doesn't fold autoHideDays into "emptiness" —
// an explicit revert to the default value must still count as a real change.
function isEmptyState(state: SyncedPageTabsState): boolean {
  return (
    !state.order.length &&
    !state.hidden.length &&
    !state.pinned.length &&
    !Object.keys(state.lastUsedAt).length
  )
}

/**
 * Two-way sync between the local page-tabs atoms (localStorage) and the backend's
 * generic component-preference store, so tab order, hidden/pinned pages, last-used
 * timestamps, and the auto-hide threshold all follow the user across devices. The
 * server copy wins on load; local changes are pushed up debounced. Order rides the
 * preference record's `columnOrder` field (as before); the other four fields are
 * JSON-encoded into its generic `config` string map.
 */
export function usePageTabsSync({
  isLoggedIn,
  userId,
}: {
  isLoggedIn: boolean
  userId?: string | null
}) {
  const [order, setOrder] = useAtom(pageTabsOrderAtom)
  const [hidden, setHidden] = useAtom(hiddenPageIdsAtom)
  const [pinned, setPinned] = useAtom(pinnedPageIdsAtom)
  const [lastUsedAt, setLastUsedAt] = useAtom(pageLastUsedAtAtom)
  const [autoHideDays, setAutoHideDays] = useAtom(pageAutoHideDaysAtom)

  const prefsRef = useRef<SyncedPrefs>({ order, hidden, pinned, autoHideDays })
  prefsRef.current = { order, hidden, pinned, autoHideDays }

  useEffect(() => {
    if (!isLoggedIn || !userId || hydratedUserKey === userId) return

    // A different user hydrating in this same JS session (auth switch in another tab,
    // or a reset that missed this tab) must not inherit — or upload — the previous
    // user's in-memory state.
    let localPrefs = prefsRef.current
    if (hydratedUserKey && hydratedUserKey !== userId) {
      localPrefs = EMPTY_PREFS
      setOrder(EMPTY_PREFS.order)
      setHidden(EMPTY_PREFS.hidden)
      setPinned(EMPTY_PREFS.pinned)
      setAutoHideDays(EMPTY_PREFS.autoHideDays)
      setLastUsedAt({})
    }
    hydratedUserKey = userId
    saveArmedUserKey = null
    lastSyncedSnapshot = null
    const localPrefsAtFetch = JSON.stringify(localPrefs)

    getComponentPreference(PAGE_TABS_COMPONENT_ID)
      .then((pref) => {
        const serverPrefs: SyncedPrefs = {
          order: pref?.columnOrder ?? [],
          hidden: parseConfigField(pref?.config, 'hiddenPageIds', [] as string[]),
          pinned: parseConfigField(pref?.config, 'pinnedPageIds', [] as string[]),
          autoHideDays: parseAutoHideDays(pref?.config),
        }
        const serverLastUsedAt = parseConfigField(
          pref?.config,
          'lastUsedAt',
          {} as Record<string, number>
        )

        if (!isEmptyPrefs(serverPrefs)) {
          // If the user changed order/hidden/pinned/autoHideDays locally while this
          // fetch was in flight, their fresher copy beats the (older) server copy —
          // the save effect uploads it.
          if (JSON.stringify(prefsRef.current) === localPrefsAtFetch) {
            if (serverPrefs.order.length) setOrder(serverPrefs.order)
            setHidden(serverPrefs.hidden)
            setPinned(serverPrefs.pinned)
            setAutoHideDays(serverPrefs.autoHideDays)
          }
        }

        // lastUsedAt drifts on its own, so it's merged unconditionally (max of
        // local/server per page) instead of gated behind the prefs check above.
        if (Object.keys(serverLastUsedAt).length) {
          setLastUsedAt((prevLocal) => {
            const merged = { ...prevLocal }
            for (const [pageId, serverTs] of Object.entries(serverLastUsedAt)) {
              if ((merged[pageId] ?? 0) < serverTs) merged[pageId] = serverTs
            }
            return merged
          })
        }

        lastSyncedSnapshot = JSON.stringify({
          order: serverPrefs.order,
          hidden: serverPrefs.hidden,
          pinned: serverPrefs.pinned,
          lastUsedAt: serverLastUsedAt,
          autoHideDays: serverPrefs.autoHideDays,
        })
      })
      .catch((err) => {
        hydratedUserKey = null // retry hydration on the next mount
        console.warn('[pageTabsSync] failed to load preferences', err)
      })
      .finally(() => {
        // Arm saves even when hydration failed: from here on, any change is an
        // explicit user action in this session, which is the best copy we have.
        saveArmedUserKey = userId
      })
  }, [isLoggedIn, userId, setOrder, setHidden, setPinned, setLastUsedAt, setAutoHideDays])

  useEffect(() => {
    if (!isLoggedIn || !userId || saveArmedUserKey !== userId) return
    const snapshot: SyncedPageTabsState = { order, hidden, pinned, lastUsedAt, autoHideDays }
    if (isEmptyState(snapshot)) return
    const serialized = JSON.stringify(snapshot)
    if (serialized === lastSyncedSnapshot) return

    const timer = setTimeout(() => {
      lastSyncedSnapshot = serialized
      saveComponentPreference({
        componentId: PAGE_TABS_COMPONENT_ID,
        columnOrder: order,
        config: {
          hiddenPageIds: JSON.stringify(hidden),
          pinnedPageIds: JSON.stringify(pinned),
          lastUsedAt: JSON.stringify(lastUsedAt),
          autoHideDays: JSON.stringify(autoHideDays),
        },
      }).catch((err) => {
        lastSyncedSnapshot = null // let the next change retry the upload
        console.warn('[pageTabsSync] failed to save preferences', err)
      })
    }, SAVE_DEBOUNCE_MS)
    return () => clearTimeout(timer)
  }, [isLoggedIn, userId, order, hidden, pinned, lastUsedAt, autoHideDays])
}
